import { assertRequestOwner } from './generation-request.js';
import { storageErrorMessage } from './session-snapshot.js';
import { recoveredSensitiveLabels, lookupProjectSensitive } from './session-recovery.js';

export const VIDEO_TERMINAL_STATES = ['failed', 'canceled', 'saved'];
const keyOf = (record) => `${record.accountId}:${record.favoriteId}`;
export const isVideoSubmissionUnresolved = (record) => !!record && !VIDEO_TERMINAL_STATES.includes(record.status);

export function unresolvedVideoError(message, projectId) {
  return Object.assign(new Error(message), { code: 'VIDEO_RESULT_UNVERIFIED', unverified: true, projectId });
}

/** A fresh attempt has its own exact lineage, including retries of the same favorite. */
export function createVideoSubmissionAttempt(request, item, { attemptId = crypto.randomUUID(), now = Date.now() } = {}) {
  return {
    accountId: request.accountId, favoriteId: item.id, attemptId,
    attribution: { workloadKind: 'direct', agentSurface: 'native_web', executionMode: 'browser',
      operationScope: 'child', rootOperationId: request.id, parentOperationId: attemptId },
    metadata: { modelId: item.options.modelId, prompt: item.options.prompt, settings: { ...item.options } },
    projectId: null, status: 'submitting', startedAt: now, updatedAt: now
  };
}

/** Public SDK sync records preserve the original flat request lineage in base64 JSON. */
export function matchVideoSubmissionProject(record, synced) {
  const ids = new Set();
  for (const project of [...(synced.snapshot?.activeProjects || []), ...(synced.snapshot?.unclaimedCompletedProjects || [])]) {
    if (!project.id || typeof project.clientRequestData !== 'string') continue;
    let original;
    try {
      const bytes = Uint8Array.from(atob(project.clientRequestData), character => character.charCodeAt(0));
      original = JSON.parse(new TextDecoder().decode(bytes));
    } catch { continue; }
    if (original?.rootOperationId === record.attribution.rootOperationId
      && original.parentOperationId === record.attribution.parentOperationId) ids.add(project.id);
  }
  return ids.size === 1 ? [...ids][0] : null;
}

/** Local journal reads never contact the SDK. Only check() performs a user-requested lookup. */
export function createVideoSubmissionJournal({ load, begin, update, now = Date.now }) {
  const records = new Map();
  const accountStates = new Map();
  const listeners = new Set();
  const notify = () => { for (const listener of listeners) listener(); };
  const state = (accountId) => accountStates.get(accountId) || { ready: false, loading: false, error: null };
  const setState = (accountId, changes) => {
    accountStates.set(accountId, { ...state(accountId), ...changes });
    notify();
  };
  const owner = (accountId, getAccountId) => assertRequestOwner({ accountId }, getAccountId());
  const storageFailure = (accountId, error) => {
    const message = `Video submission records could not be saved. ${storageErrorMessage(error)} Keep this tab open; no automatic retry will run.`;
    setState(accountId, { error: message });
    return Object.assign(new Error(message), { code: 'VIDEO_STORAGE_UNAVAILABLE', cause: error });
  };

  async function change(record, changes) {
    const key = keyOf(record);
    const current = records.get(key);
    if (!current || current.attemptId !== record.attemptId) return false;
    const next = { ...current, ...changes, updatedAt: now() };
    records.set(key, next);
    notify();
    try {
      const saved = await update(next.accountId, next.favoriteId, next.attemptId, changes);
      if (!saved) throw new Error('This video submission record changed in another tab. Reload its saved record before submitting again.');
      return true;
    } catch (error) {
      storageFailure(record.accountId, error);
      return false;
    }
  }

  return {
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    getState(accountId) { return state(accountId); },
    invalidate(message) {
      for (const accountId of accountStates.keys()) setState(accountId, { ready: false, loading: false, error: message });
    },
    get(accountId, favoriteId) { return records.get(`${accountId}:${favoriteId}`); },
    unresolved(favorites, accountId) {
      return favorites.flatMap(favorite => {
        const record = records.get(`${accountId}:${favorite.id}`);
        return isVideoSubmissionUnresolved(record) ? [{ ...record, id: favorite.id }] : [];
      });
    },
    assertNewSelection(favorites, accountId) {
      const existing = this.unresolved(favorites, accountId)[0];
      if (existing) throw unresolvedVideoError('A video for a selected image is still awaiting confirmation or its download link. Check the existing project in your Sogni creations and unselect that image before generating others.', existing.projectId);
    },
    assertReady(accountId) {
      if (!state(accountId).ready || state(accountId).loading) {
        throw Object.assign(new Error(state(accountId).error || 'Load saved video submission records before generating.'), { code: 'VIDEO_STORAGE_UNAVAILABLE' });
      }
    },
    async hydrate(accountId, { getAccountId }) {
      owner(accountId, getAccountId);
      const observed = new Map(records);
      setState(accountId, { ready: false, loading: true, error: null });
      try {
        const saved = await load(accountId);
        owner(accountId, getAccountId);
        for (const record of saved) {
          if (record.accountId !== accountId) continue;
          const local = records.get(keyOf(record));
          // An ACK or a new attempt written while this read was awaiting must
          // not be replaced by the read's earlier snapshot.
          if (local && local !== observed.get(keyOf(record))) continue;
          // A later failed write can leave the current tab with a newer known ID.
          if (local?.attemptId === record.attemptId && local.updatedAt > record.updatedAt) continue;
          records.set(keyOf(record), record);
        }
        setState(accountId, { ready: true, loading: false });
      } catch (error) {
        setState(accountId, { ready: false, loading: false });
        if (getAccountId() === accountId) storageFailure(accountId, error);
        throw error;
      }
    },
    async start(record, reviewed, { getAccountId }) {
      owner(record.accountId, getAccountId);
      this.assertReady(record.accountId);
      this.assertNewSelection([{ id: record.favoriteId }], record.accountId);
      // Keep concrete settings, never the approval/quote or page object URLs.
      record = { ...record, metadata: { ...record.metadata,
        settings: { ...record.metadata.settings, billingMode: reviewed.projectConfig.billingMode } } };
      try { await begin(record); }
      catch (error) {
        if (error.code === 'VIDEO_RESULT_UNVERIFIED') {
          if (error.submission?.accountId === record.accountId && error.submission.favoriteId === record.favoriteId) {
            records.set(keyOf(record), error.submission);
            notify();
          }
          throw error;
        }
        throw storageFailure(record.accountId, error);
      }
      records.set(keyOf(record), record);
      notify();
      owner(record.accountId, getAccountId);
    },
    accepted(record, projectId) { return change(record, { projectId, status: 'accepted' }); },
    failed(record, error, { createAttempted, projectStatus } = {}) {
      const unresolved = error.unverified === true || (createAttempted && projectStatus === 'completed')
        || error.originalCode === 'projectLost';
      return change(record, { ...(error.projectId ? { projectId: error.projectId } : {}),
        status: unresolved ? 'unverified' : projectStatus === 'canceled' ? 'canceled' : 'failed' });
    },
    saved(record) {
      const current = records.get(keyOf(record));
      if (!current || current.attemptId !== record.attemptId) return;
      // saveVideo committed the native Blob and this marker in one transaction.
      records.set(keyOf(record), { ...current, status: 'saved', updatedAt: now() });
      notify();
    },
    /** One sync for a missing ACK, then one result read; never creates or polls. */
    async check(favoriteId, accountId, { projects, getAccountId }) {
      owner(accountId, getAccountId);
      const record = records.get(`${accountId}:${favoriteId}`);
      if (!isVideoSubmissionUnresolved(record)) throw unresolvedVideoError('No unresolved video submission is saved for this image.');
      let projectId = record.projectId;
      // The result lookup drops the sensitive label: the raw synced record carries it
      // for a matched project, and a known project is read once below.
      let sensitive = null;
      if (!projectId) {
        const synced = await projects.sync('manual');
        owner(accountId, getAccountId);
        projectId = matchVideoSubmissionProject(record, synced);
        if (!projectId) throw unresolvedVideoError('This video submission could not be matched to one existing project. Check your Sogni creations before generating it again.');
        // A matched project still running at the sync has no label yet: read it at completion.
        sensitive = recoveredSensitiveLabels(synced).has(projectId) ? true : null;
        await change(record, { projectId, status: 'unverified' });
        owner(accountId, getAccountId);
      }
      const result = await projects.getResult(projectId, { kind: 'video' });
      owner(accountId, getAccountId);
      if (['failed', 'canceled'].includes(result.status)) {
        const saved = await change(record, { projectId, status: result.status });
        owner(accountId, getAccountId);
        if (!saved) throw Object.assign(new Error(state(accountId).error), { code: 'VIDEO_STORAGE_UNAVAILABLE' });
        return { ...record, id: favoriteId, projectId, status: result.status, retryable: true };
      }
      const completed = result.jobs?.find(job => job.status === 'completed' && job.url);
      if (result.status === 'completed' && completed) {
        if (sensitive === null) {
          sensitive = await lookupProjectSensitive(projects, projectId);
          owner(accountId, getAccountId);
        }
        // The native Blob save commits the terminal marker atomically. A missing
        // or failed download must retain the known ID for the next page load.
        await change(record, { projectId, status: 'unverified' });
        owner(accountId, getAccountId);
        return { ...record, id: favoriteId, projectId, status: 'completed', videoUrl: completed.url, sensitive };
      }
      throw unresolvedVideoError('This existing video is still awaiting confirmation or its download link. Check your Sogni creations or try this check again later.', projectId);
    }
  };
}
